import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { LoaderService } from 'gutwin-shared';

import { Template } from '@gutwin-audit/shared/models/template.model';

import { AuditService } from '@gutwin-audit/shared/services/audit.service';
import { FilterModuleService } from '@gutwin-audit/shared/services/filter-module.service';
import { TemplateService } from '@gutwin-audit/shared/services/template.service';

@Component({
  selector: 'gw-templates',
  templateUrl: './templates.component.html'
})
export class TemplatesComponent implements OnInit, OnDestroy {
  templates: Array<Template>;
  totalTemplates: number;
  notify = {
    error: {
      connectionTitle: '',
      loadTemplates: '',
      loadAudits: ''
    }
  };
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private auditService: AuditService,
    private loaderService: LoaderService,
    private notificationsService: NotificationsService,
    private route: ActivatedRoute,
    private templateService: TemplateService,
    private translateService: TranslateService,
    public filterService: FilterModuleService
  ) {}

  ngOnInit() {
    if (this.route.snapshot.data['templates']) {
      this.updateData(this.route.snapshot.data['templates']);
    }
    this.loadAuditsAmount();
    this.observeFilters();
    this.fetchTranslate();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  observeFilters(): Subscription {
    return this.filterService.filterObservable
      .pipe(takeUntil(this.destroy$), debounceTime(200))
      .subscribe(({ page, limit, filters }) => {
        this.loadTemplates(page, filters, limit);
        this.loadAuditsAmount(page, filters, limit);
      });
  }

  updateData(data: { templates: Array<Template> }): void {
    this.templates = data.templates;
    this.totalTemplates = this.templateService.templatesAmount.filtered;
  }

  loadTemplates(
    page = this.filterService.page,
    filters = this.filterService.auditsFilters,
    limit = this.filterService.limit
  ): void {
    this.loaderService.show('auditsLoader');
    this.templateService
      .getTemplates(page, filters, limit)
      .then((data: { templates: Array<Template> }) => {
        this.updateData(data);
        this.loaderService.hide('auditsLoader');
      })
      .catch(error => {
        this.notificationsService.error(this.notify.error.connectionTitle, this.notify.error.loadTemplates);
        this.loaderService.hide('auditsLoader');
      });
  }

  loadAuditsAmount(
    page = this.filterService.page,
    filters = this.filterService.auditsFilters,
    limit = this.filterService.limit
  ): void {
    this.auditService.getAuditsInProgress(page, filters, limit).catch(error => {
      this.notificationsService.error(this.notify.error.connectionTitle, this.notify.error.loadAudits);
    });
  }

  changePage(page: number): void {
    this.filterService.setPage(page);
  }

  changeLimit(limit: number): void {
    this.filterService.setLimit(limit);
  }

  onUpdateTemplate(): void {
    this.loadTemplates();
  }

  onRemoveTemplate(): void {
    if (this.templates.length > 1) {
      this.loadTemplates();
    } else {
      this.filterService.setFilters(this.filterService.page - 1);
    }
  }

  async fetchTranslate(): Promise<void> {
    const translation = await this.translateService
      .get(['GLOBAL.ERROR.CONNECTION', 'TEMPLATES_LIST.ERROR.LOAD_TEMPLATES', 'AUDITS_LIST.ERROR.LOAD_AUDITS_AMOUNT'])
      .toPromise();
    this.notify.error.connectionTitle = translation['GLOBAL.ERROR.CONNECTION'];
    this.notify.error.loadTemplates = translation['TEMPLATES_LIST.ERROR.LOAD_TEMPLATES'];
    this.notify.error.loadAudits = translation['AUDITS_LIST.ERROR.LOAD_AUDITS_AMOUNT'];
  }
}
